/**
 * useLocalStorage Hook with Schema Versioning & Migration
 * 
 * Features:
 * - SSR-safe (Next.js compatible)
 * - Schema versioning with automatic migration
 * - Corrupted data handling with fallback
 * - Cross-tab synchronization via storage events
 * - TypeScript type safety
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Check if we're in a browser environment (SSR-safe)
const isBrowser = typeof window !== 'undefined';

export interface StorageSchema<T> {
  version: number;
  data: T;
  lastUpdated: number;
}

export interface StorageOptions<T> {
  key: string;
  defaultValue: T;
  version: number;
  migrate?: (oldData: any, oldVersion: number, newVersion: number) => T;
  serialize?: (value: T) => string;
  deserialize?: (value: string) => T;
  syncAcrossTabs?: boolean;
}

/**
 * Custom hook for localStorage with versioning and migration
 * 
 * @param options Configuration options for storage
 * @returns [storedValue, setValue, removeValue]
 */
export function useLocalStorage<T>(
  options: StorageOptions<T>
): [T, (value: T | ((val: T) => T)) => void, () => void] {
  const {
    key,
    defaultValue,
    version,
    migrate,
    serialize = JSON.stringify,
    deserialize = JSON.parse,
    syncAcrossTabs = true,
  } = options;

  // Track if we've initialized from localStorage
  const initialized = useRef(false);
  
  // Initialize state from localStorage (SSR-safe)
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (!isBrowser) {
      return defaultValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      
      if (!item) {
        console.log(`[useLocalStorage] No data found for key: ${key}, using default`);
        return defaultValue;
      }

      const parsed: StorageSchema<T> = deserialize(item);
      
      // Validate schema structure
      if (!parsed || typeof parsed.version !== 'number' || !parsed.data) {
        console.warn(`[useLocalStorage] Invalid schema structure for key: ${key}`);
        return defaultValue;
      }

      // Check if migration is needed
      if (parsed.version < version) {
        console.log(`[useLocalStorage] Migrating ${key} from v${parsed.version} to v${version}`);
        
        if (migrate) {
          try {
            const migratedData = migrate(parsed.data, parsed.version, version);
            console.log(`[useLocalStorage] Migration successful for ${key}`);
            return migratedData;
          } catch (migrationError) {
            console.error(`[useLocalStorage] Migration failed for ${key}:`, migrationError);
            return defaultValue;
          }
        } else {
          console.warn(`[useLocalStorage] No migration function provided for ${key}`);
          return defaultValue;
        }
      }

      // Check if version is newer (data from future version)
      if (parsed.version > version) {
        console.warn(`[useLocalStorage] Data version (${parsed.version}) is newer than current (${version}) for ${key}`);
        // Optionally use the data anyway or return default
        return parsed.data;
      }

      console.log(`[useLocalStorage] Loaded ${key} v${parsed.version}`);
      return parsed.data;
      
    } catch (error) {
      console.error(`[useLocalStorage] Error loading ${key} from localStorage:`, error);
      
      // Try to clear corrupted data
      try {
        window.localStorage.removeItem(key);
        console.log(`[useLocalStorage] Cleared corrupted data for ${key}`);
      } catch (clearError) {
        console.error(`[useLocalStorage] Error clearing corrupted data:`, clearError);
      }
      
      return defaultValue;
    }
  });

  // Set value to localStorage
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        // Allow value to be a function (like useState)
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        // Update state
        setStoredValue(valueToStore);

        // Save to localStorage (SSR-safe)
        if (isBrowser) {
          const schema: StorageSchema<T> = {
            version,
            data: valueToStore,
            lastUpdated: Date.now(),
          };
          
          window.localStorage.setItem(key, serialize(schema));
          
          console.log(`[useLocalStorage] Saved ${key} v${version} at ${new Date().toLocaleTimeString()}`);
        }
      } catch (error) {
        // Handle quota exceeded or other errors
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          console.error(`[useLocalStorage] Storage quota exceeded for ${key}`);
          
          // Attempt cleanup of old keys
          try {
            if (isBrowser) {
              const keysToRemove: string[] = [];
              for (let i = 0; i < localStorage.length; i++) {
                const storageKey = localStorage.key(i);
                if (storageKey && storageKey.startsWith('trekova:old:')) {
                  keysToRemove.push(storageKey);
                }
              }
              
              keysToRemove.forEach(k => localStorage.removeItem(k));
              
              if (keysToRemove.length > 0) {
                console.log(`[useLocalStorage] Cleaned up ${keysToRemove.length} old keys`);
                // Retry save
                const schema: StorageSchema<T> = {
                  version,
                  data: value instanceof Function ? value(storedValue) : value,
                  lastUpdated: Date.now(),
                };
                window.localStorage.setItem(key, serialize(schema));
              }
            }
          } catch (cleanupError) {
            console.error(`[useLocalStorage] Cleanup failed:`, cleanupError);
          }
        } else {
          console.error(`[useLocalStorage] Error saving ${key}:`, error);
        }
      }
    },
    [key, version, serialize, storedValue]
  );

  // Remove value from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(defaultValue);
      
      if (isBrowser) {
        window.localStorage.removeItem(key);
        console.log(`[useLocalStorage] Removed ${key}`);
      }
    } catch (error) {
      console.error(`[useLocalStorage] Error removing ${key}:`, error);
    }
  }, [key, defaultValue]);

  // Listen for storage changes from other tabs
  useEffect(() => {
    if (!isBrowser || !syncAcrossTabs) {
      return;
    }

    const handleStorageChange = (e: StorageEvent) => {
      // Only respond to changes to our key
      if (e.key !== key) {
        return;
      }

      console.log(`[useLocalStorage] Storage event detected for ${key} from another tab`);

      try {
        if (e.newValue === null) {
          // Key was removed
          console.log(`[useLocalStorage] ${key} was removed in another tab`);
          setStoredValue(defaultValue);
        } else {
          // Key was updated
          const parsed: StorageSchema<T> = deserialize(e.newValue);
          
          if (parsed && parsed.version === version) {
            console.log(`[useLocalStorage] Syncing ${key} from another tab`);
            setStoredValue(parsed.data);
          } else if (parsed && parsed.version < version && migrate) {
            // Migrate data from other tab
            const migratedData = migrate(parsed.data, parsed.version, version);
            setStoredValue(migratedData);
            console.log(`[useLocalStorage] Migrated and synced ${key} from another tab`);
          } else if (parsed) {
            setStoredValue(parsed.data);
          }
        }
      } catch (error) {
        console.error(`[useLocalStorage] Error handling storage event for ${key}:`, error);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, version, defaultValue, deserialize, migrate, syncAcrossTabs]);

  return [storedValue, setValue, removeValue];
}

/**
 * Helper function to get a value from localStorage without using the hook
 * Useful for one-time reads or outside React components
 */
export function getLocalStorageItem<T>(
  key: string,
  defaultValue: T,
  version: number,
  migrate?: (oldData: any, oldVersion: number, newVersion: number) => T
): T {
  if (!isBrowser) {
    return defaultValue;
  }

  try {
    const item = window.localStorage.getItem(key);
    
    if (!item) {
      return defaultValue;
    }

    const parsed: StorageSchema<T> = JSON.parse(item);
    
    if (!parsed || typeof parsed.version !== 'number' || !parsed.data) {
      return defaultValue;
    }

    if (parsed.version < version && migrate) {
      return migrate(parsed.data, parsed.version, version);
    }

    return parsed.data;
    
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
}

/**
 * Helper function to set a value in localStorage without using the hook
 */
export function setLocalStorageItem<T>(
  key: string,
  value: T,
  version: number
): void {
  if (!isBrowser) {
    return;
  }

  try {
    const schema: StorageSchema<T> = {
      version,
      data: value,
      lastUpdated: Date.now(),
    };
    
    window.localStorage.setItem(key, JSON.stringify(schema));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
}

